/**
 * Internal Job Board — open positions visible to ALL employees.
 * Employees can refer colleagues via the existing candidate add flow.
 */
import React, { useEffect, useState } from 'react';
import { apiGet, apiPost } from '../../../lib/api';
import { toast } from 'sonner';
import { Briefcase, MapPin, Clock, Users, Plus, X, ChevronRight, Upload } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const daysLeft = (closing) => {
  if (!closing) return null;
  const diff = Math.ceil((new Date(closing) - new Date()) / 86400000);
  return diff;
};

export default function JobBoard() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referJob, setReferJob] = useState(null);  // job being referred to
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiGet('/hr/jobs/internal-board')
      .then((d) => setJobs(d.jobs || []))
      .catch(() => toast.error('Failed to load open positions'))
      .finally(() => setLoading(false));
  }, []);

  const submitReferral = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Candidate name is required'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name.trim());
      if (form.email) fd.append('email', form.email.trim());
      if (form.phone) fd.append('phone', form.phone.trim());
      fd.append('source', 'referral');
      if (user?.employee_id) fd.append('referred_by_employee_id', user.employee_id);
      if (resume) fd.append('resume', resume);
      const token = localStorage.getItem('token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hr/jobs/${referJob.id}/candidates`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) {
        const d = await r.json();
        throw new Error(d.detail || 'Failed');
      }
      toast.success(`Referral submitted for ${referJob.title}`);
      setReferJob(null);
      setForm({ name: '', email: '', phone: '' });
      setResume(null);
    } catch (e) { toast.error(e.message || 'Failed to submit referral'); }
    setSubmitting(false);
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-5xl" data-testid="job-board-page">
      <div>
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">People & HR</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Open Positions</h1>
        <p className="text-sm text-slate-600 mt-1">Know someone great? Refer them to an open role.</p>
      </div>

      {loading && <div className="text-slate-500 text-sm">Loading…</div>}

      {!loading && jobs.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center text-slate-400">
          <Briefcase size={28} className="mx-auto mb-3 opacity-40" />
          <div className="font-semibold">No open positions at the moment.</div>
          <div className="text-xs mt-1">Check back soon.</div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {jobs.map((j) => {
          const dl = daysLeft(j.closing_date);
          const urgent = dl !== null && dl <= 7;
          return (
            <div key={j.id} className="bg-white border border-slate-200 rounded-sm hover:border-[#1F7A6E]/40 transition-colors" data-testid={`job-card-${j.id}`}>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-display font-bold text-base">{j.title}</div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-3 flex-wrap">
                      {j.department && <span className="flex items-center gap-1"><Users size={11} /> {j.department}</span>}
                      {j.location && <span className="flex items-center gap-1"><MapPin size={11} /> {j.location}</span>}
                      {j.min_experience_years && <span>{j.min_experience_years}+ yrs exp</span>}
                    </div>
                  </div>
                  {j.headcount > 1 && (
                    <span className="text-[11px] font-bold bg-[#1F7A6E]/10 text-[#1F7A6E] px-2 py-0.5 rounded-sm shrink-0">{j.headcount} openings</span>
                  )}
                </div>

                {j.jd_summary && (
                  <p className="text-xs text-slate-600 mt-2.5 line-clamp-2">{j.jd_summary}</p>
                )}

                {(j.required_skills || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {j.required_skills.slice(0, 5).map((s, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-600 rounded-sm font-display font-bold">{s}</span>
                    ))}
                    {j.required_skills.length > 5 && <span className="text-[10px] text-slate-400">+{j.required_skills.length - 5} more</span>}
                  </div>
                )}

                <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                  {j.closing_date ? (
                    <span className={`text-[11px] flex items-center gap-1 ${urgent ? 'text-red-600 font-bold' : 'text-slate-400'}`}>
                      <Clock size={11} /> Closes {fmtDate(j.closing_date)}{dl !== null && dl >= 0 ? ` (${dl}d)` : ' (expired)'}
                    </span>
                  ) : <span />}
                  <button onClick={() => { setReferJob(j); setForm({ name: '', email: '', phone: '' }); setResume(null); }}
                    className="flex items-center gap-1.5 text-xs font-display font-bold text-[#1F7A6E] hover:underline"
                    data-testid={`refer-btn-${j.id}`}>
                    <Plus size={13} /> Refer a Colleague
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Referral modal */}
      {referJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="referral-modal">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-bold text-base">Refer a Colleague</div>
                <div className="text-xs text-slate-500 mt-0.5">{referJob.title} · {referJob.department}</div>
              </div>
              <button onClick={() => setReferJob(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>
            <form onSubmit={submitReferral} className="space-y-3">
              <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Full Name *</label><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Candidate name" data-testid="refer-name" /></div>
              <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Email</label><input type="email" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="candidate@email.com" /></div>
              <div><label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Phone</label><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">Resume (optional)</label>
                <label className={`flex items-center gap-2 border-2 border-dashed rounded-sm px-3 py-2.5 cursor-pointer text-xs text-slate-500 hover:border-[#1F7A6E] ${resume ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-slate-300'}`}>
                  <Upload size={14} />
                  {resume ? resume.name : 'Click to upload resume (PDF)'}
                  <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={e => setResume(e.target.files?.[0] || null)} />
                </label>
              </div>
              <div className="flex gap-2 pt-1">
                <button type="submit" disabled={submitting}
                  className="flex-1 py-2.5 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider disabled:opacity-50 flex items-center justify-center gap-1.5"
                  data-testid="submit-referral-btn">
                  {submitting ? 'Submitting…' : <><ChevronRight size={13} /> Submit Referral</>}
                </button>
                <button type="button" onClick={() => setReferJob(null)} className="px-4 py-2.5 border border-slate-300 text-slate-600 rounded-sm text-xs font-bold">Cancel</button>
              </div>
            </form>
            <p className="text-[11px] text-slate-400 text-center">Your name will be recorded as the referrer. HR will contact you if shortlisted.</p>
          </div>
        </div>
      )}
    </div>
  );
}
